import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { showWarning } from 'src/utils/toast/toastUtil';
import {
	CreateCellClickHandler,
	CustomCellClickAction,
	createCellCssClassRules,
	GridCellConfig,
	useCellChangeHandler,
} from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import {
	getInspectionValueValidationMessage,
	INSPECTION_VALUE_TYPE,
	isValidInspectionValue,
	normalizeInspectionValue,
} from 'src/utils/inspection/inspectionValueUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { defaultTQC2010_R01DetailDto, TQC2010_R01DetailDto } from './Dto_TQC2010_R01';
import { useTQC2010_R01Store } from './Store_TQC2010_R01';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

const COLUMN_STATE_KEY = 'TQC2010_R01-detail-grid-column-state';

const headerNames: Record<string, string> = {
	rowIndex: '',
	inspTypeNm: '검사구분',
	inspItemCd: '항목코드',
	inspItemNm: '항목명',
	specValue1: '값1',
	specRangeNm: '범위',
	specValue2: '값2',
	inspUnit: '단위',
	valueTypeNm: '값유형',
	inspClassNm: '검사방법',
	resultValue1: '결과값',
	remark: '비고',
};

const EDITABLE_COLUMNS = ['resultValue1'];

interface Props {
	detailGridRef: React.RefObject<AgGridReact<TQC2010_R01DetailDto> | null>;
}

export const DetailTable_TQC2010_R01 = ({ detailGridRef }: Props) => {
	const {
		TQC2010_R01DetailList,
		updateTQC2010_R01DetailList,
		originalTQC2010_R01DetailList,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useTQC2010_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createCellClassRules,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: TQC2010_R01DetailList,
		defaultGridData: defaultTQC2010_R01DetailDto,
		selectedRow: [],
		originalGridData: originalTQC2010_R01DetailList,
		updateRow: (index: number, data: any) => updateTQC2010_R01DetailList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [
			'inspTypeNm', 'inspItemCd', 'inspItemNm',
			'specValue1', 'specRangeNm', 'specValue2', 'inspUnit', 'valueTypeNm', 'inspClassNm', 'remark',
		],
		insertedDisabledCells: [],
		commonDisabledCells: [],
		pointerCells: ['resultValue1'],
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ['inspItemNm', 'remark'],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<TQC2010_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: [],
			deletedList: [],
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});
		return {
			...defaultColDef,
			cellClassRules: baseRules,
		};
	}, [
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	const handleResultValueSwitch = useCallback((
		valueIndex: number,
		params: ICellRendererParams<TQC2010_R01DetailDto>,
		newValue: 'O' | 'X',
	) => {
		if (!params.data) return;
		const original = originalTQC2010_R01DetailList.find(
			(item) => item.valueIndex === valueIndex,
		);
		updateTQC2010_R01DetailList(valueIndex, {
			...params.data,
			resultValue1: newValue,
			isModified: params.data.inserted || original?.resultValue1 !== newValue,
		});
	}, [originalTQC2010_R01DetailList, updateTQC2010_R01DetailList]);

	const baseColumnDefs: ColDef<TQC2010_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{ headerName: '검사구분', colId: 'inspTypeNm', field: 'inspTypeNm', width: 100, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '항목코드', colId: 'inspItemCd', field: 'inspItemCd', width: 100, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '항목명', colId: 'inspItemNm', field: 'inspItemNm', width: 180, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '값1', colId: 'specValue1', field: 'specValue1', width: 80, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '범위', colId: 'specRangeNm', field: 'specRangeNm', width: 70, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '값2', colId: 'specValue2', field: 'specValue2', width: 80, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '단위', colId: 'inspUnit', field: 'inspUnit', width: 70, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '값유형', colId: 'valueTypeNm', field: 'valueTypeNm', width: 90, editable: false, headerClass: aggridHeaderClass },
		{ headerName: '검사방법', colId: 'inspClassNm', field: 'inspClassNm', width: 90, editable: false, headerClass: aggridHeaderClass },
		{
			headerName: '결과값',
			colId: 'resultValue1',
			field: 'resultValue1',
			width: 90,
			editable: (params) => params.data?.valueType !== INSPECTION_VALUE_TYPE.PASS_FAIL,
			pasteable: true,
			headerClass: aggridHeaderClass,
			cellRenderer: (params: ICellRendererParams<TQC2010_R01DetailDto>) => {
				if (params.node.rowPinned === 'bottom' || !params.data) return null;
				if (params.data.valueType !== INSPECTION_VALUE_TYPE.PASS_FAIL) return params.value ?? '';
				const selectedValue = normalizeInspectionValue(
					params.data.valueType,
					params.data.resultValue1,
				);
				return (
					<div className="flex items-center justify-center gap-1 h-full">
						{(['O', 'X'] as const).map((value) => (
							<button
								key={value}
								type="button"
								className={`h-5 min-w-7 rounded border text-xs font-semibold ${
									selectedValue === value
										? 'border-blue-500 bg-blue-100 text-blue-700'
										: 'border-gray-300 bg-white text-gray-500'
								}`}
								onClick={(event) => {
									event.stopPropagation();
									handleResultValueSwitch(params.data!.valueIndex, params, value);
								}}
							>
								{value}
							</button>
						))}
					</div>
				);
			},
			valueSetter: (params) => {
				const valueType = params.data.valueType;
				const normalized = normalizeInspectionValue(valueType, params.newValue);
				if (!isValidInspectionValue(valueType, normalized)) {
					showWarning(getInspectionValueValidationMessage(valueType));
					return false;
				}
				params.data.resultValue1 = normalized;
				return true;
			},
		},
		{ headerName: '비고', colId: 'remark', field: 'remark', width: 160, editable: false, headerClass: aggridHeaderClass },
	], [handleResultValueSwitch]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

const columnDefs: ColDef<TQC2010_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<TQC2010_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState]);

	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC2010_R01DetailList,
		editableColumns: EDITABLE_COLUMNS,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const cellClickActions: CustomCellClickAction<TQC2010_R01DetailDto>[] = useMemo(() => [{
		validate: (field, data) => EDITABLE_COLUMNS.includes(field)
			&& data.valueType !== INSPECTION_VALUE_TYPE.PASS_FAIL,
		action: (e) => {
			e.api.startEditingCell({
				rowIndex: e.rowIndex!,
				colKey: e.colDef.colId ?? e.colDef.field!,
			});
		},
	}], []);

	const handleCellClick = CreateCellClickHandler(
		TQC2010_R01DetailList,
		'valueIndex',
		cellClickActions,
	);

	const handleCellChange = useCellChangeHandler({
		dataList: TQC2010_R01DetailList,
		originalDataList: originalTQC2010_R01DetailList,
		updateRow: updateTQC2010_R01DetailList,
	});

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		inspItemCd: `${TQC2010_R01DetailList.length.toLocaleString()}건`,
		editable: false,
	}]), [TQC2010_R01DetailList.length]);

	return (
		<GridContextWrapper<TQC2010_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={detailGridRef}
			paddedGridData={TQC2010_R01DetailList}
			initSelected={() => {}}
			defaultRowData={defaultTQC2010_R01DetailDto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
			enableExcelPaste={false}
		>
			<Grid_Excel<TQC2010_R01DetailDto>
				ref={detailGridRef}
				rowData={TQC2010_R01DetailList}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				getRowId={getRowId}
				onCellClicked={handleCellClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	);
};
